import Phaser from "phaser";
import { TILE, worldToScreen, type ScreenPos } from "../map/Iso";

export const WATERING_CAN_KEY = "watering-can";

const CAN_WIDTH = 28;
const CAN_HEIGHT = 20;
const ANIM_DURATION_MS = 1200;

/** Generate a simple watering-can texture procedurally. */
export function ensureWateringCanTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(WATERING_CAN_KEY)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  // Body
  g.fillStyle(0x5588cc, 1);
  g.fillRoundedRect(2, 6, 18, 12, 2);
  // Spout
  g.lineStyle(2, 0x5588cc, 1);
  g.beginPath();
  g.moveTo(20, 10);
  g.lineTo(26, 4);
  g.strokePath();
  // Handle
  g.lineStyle(2, 0x336699, 1);
  g.beginPath();
  g.moveTo(6, 6);
  g.lineTo(6, 2);
  g.lineTo(14, 2);
  g.lineTo(14, 6);
  g.strokePath();
  // Water drops
  g.fillStyle(0x66ccff, 0.8);
  g.fillCircle(25, 6, 1.5);
  g.fillCircle(27, 8, 1.5);
  g.fillCircle(24, 9, 1.5);
  g.generateTexture(WATERING_CAN_KEY, CAN_WIDTH, CAN_HEIGHT);
  g.destroy();
}

/**
 * Plays a watering-can animation at a world-tile position.
 * Shows the can tilting and water drops falling, then fades out.
 */
export function playWateringAnimation(
  scene: Phaser.Scene,
  origin: ScreenPos,
  worldX: number,
  worldY: number,
  onComplete?: () => void,
): void {
  const { sx, sy } = worldToScreen(worldX, worldY);
  const screenX = origin.sx + sx + TILE.halfW + 12;
  const screenY = origin.sy + sy - 8;

  const can = scene.add.image(screenX, screenY, WATERING_CAN_KEY);
  can.setOrigin(0.5, 1);
  can.setDepth((worldX + worldY) * 1000 + 2);
  can.setAlpha(0);

  // Water drops container
  const drops: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < 5; i++) {
    const drop = scene.add.circle(screenX + 6, screenY - 4, 2, 0x66ccff, 0);
    drop.setDepth((worldX + worldY) * 1000 + 2);
    drops.push(drop);
  }

  // Fade in the can
  scene.tweens.add({
    targets: can,
    alpha: 1,
    angle: -15,
    duration: 200,
    ease: "Sine.easeOut",
  });

  // Animate water drops falling
  drops.forEach((drop, i) => {
    scene.tweens.add({
      targets: drop,
      alpha: { from: 0, to: 0.9 },
      y: drop.y + 20 + Math.random() * 10,
      x: drop.x + (Math.random() - 0.5) * 12,
      duration: 500,
      delay: 300 + i * 120,
      ease: "Quad.easeIn",
      onComplete: () => drop.destroy(),
    });
  });

  // Fade out the can and call onComplete
  scene.tweens.add({
    targets: can,
    alpha: 0,
    delay: ANIM_DURATION_MS - 300,
    duration: 300,
    ease: "Sine.easeIn",
    onComplete: () => {
      can.destroy();
      onComplete?.();
    },
  });
}
